import { IdentityPicker } from '@/features/OrgMembers/components/IdentityPicker'
import {
  type MemberRow,
  isRowBlocked,
} from '@/features/OrgMembers/lib/memberRows'
import { Button, HStack, Stack, Text } from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

const KEY_PREFIX = 'accountManagement.createAccount.owner'

type OwnerLookupStatusProps = Readonly<{
  owner: MemberRow
  disabled: boolean
  onRetry: () => void
  onSelectIdentity: (userId: string) => void
}>

function statusText(owner: MemberRow, t: TFunction): string {
  switch (owner.status) {
    case 'pending':
      return String(t(`${KEY_PREFIX}.pending`))
    case 'new':
      return String(t(`${KEY_PREFIX}.new`))
    case 'existing':
      return String(
        t(`${KEY_PREFIX}.existing`, {
          connection: owner.identity.connection ?? owner.identity.type,
          verification: t(
            owner.identity.emailVerified
              ? `${KEY_PREFIX}.verified`
              : `${KEY_PREFIX}.unverified`,
          ),
        }),
      )
    case 'multiple':
      return String(t(`${KEY_PREFIX}.multiple`))
    case 'ssoDomainNoIdentity':
      if (owner.ssoDomainClaim) {
        return String(
          t(`${KEY_PREFIX}.ssoDomainNoIdentity`, {
            connection: owner.ssoDomainClaim.connectionName,
          }),
        )
      }
      return String(t(`${KEY_PREFIX}.ssoDomainNoIdentityUnnamed`))
    case 'noSelectableIdentity':
      return String(t(`${KEY_PREFIX}.noSelectableIdentity`))
    case 'lookupFailed':
      return String(t(`${KEY_PREFIX}.lookupFailed`))
    default:
      return ''
  }
}

export function OwnerLookupStatus({
  owner,
  disabled,
  onRetry,
  onSelectIdentity,
}: OwnerLookupStatusProps) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <Stack gap={2}>
      <HStack gap={2} alignItems="center">
        <Text
          fontSize="sm"
          color={isRowBlocked(owner) ? 'fg.error' : 'text.text-subtle'}
        >
          {statusText(owner, t)}
        </Text>
        {owner.status === 'lookupFailed' && (
          <Button
            size="xs"
            variant="outline"
            disabled={disabled}
            onClick={onRetry}
          >
            {t(`${KEY_PREFIX}.retry`)}
          </Button>
        )}
      </HStack>
      {owner.status === 'multiple' && (
        <IdentityPicker
          identities={owner.identities}
          selectedUserId={owner.selectedUserId}
          disabled={disabled}
          onSelect={onSelectIdentity}
        />
      )}
    </Stack>
  )
}
